<template>
  <v-sheet
    tag="aside"
    class="above-panel"
    :class="{
      'has-overhang': hasOverhang,
      'has-content': isOpen,
      'has-scrolled-sibling': hasScrollbar,
      'is-compact': isCompact,
    }"
    :elevation="isAtTop ? 0 : 4"
  >
    <portal-target
      ref="above-panel-content"
      name="app-above-panel"
      multiple
      tag="div"
      class="above-panel__content"
      @change="isOpen = $event"
      :transition="transitionComponent"
      :slot-props="{ isCompact, isOpen, hasScrollbar }"
    />
  </v-sheet>
</template>

<script>
// Tightly coupled with AbovePanelItem.vue for handling multiple items.

// BUG: During hot reload in development, this component breaks because of the bound store module. Should work in production.
import layoutElement from '@/mixins/layoutElement';

export default {
  name: 'TheAppAbovePanel',
  mixins: [layoutElement('abovePanel')],
  props: {
    collapse: {
      type: Boolean,
      default: false,
    },
    hasScrollbar: {
      type: Boolean,
      default: false,
    },
    isAtTop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isCollapsing: false, // header collapse can trigger new scroll position in main, use this to prevent from reacting to itself
      lastChild: null,
      observer: null,
    };
  },
  computed: {
    transitionComponent() {
      return {
        functional: true,
        render(h, context) {
          /* Fixes Chrome bug where there is a weird jumb animation in the header when switching from
            a tab with scrollbar to one without (e.g. Project show Details -> Sub Projects) */
          // { props: { group: true, leaveAbsolute: true } },
          return h('v-slide-y-transition', context.children);
        },
      };
    },
    hasOverhang() {
      return true;
      //Commented out to fix above panel visual bug
      /*if (!this.lastChild) return false;
      return !this.lastChild.classList.contains(
        'above-panel__item--full-width'
      );*/
    },
  },
  watch: {
    hasOverhang: {
      handler(newVal) {
        this.$emit('change-overhang', newVal);
      },
    },
  },
  mounted() {
    this.initObserver();
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    initObserver() {
      // Guard against environments without MutationObserver
      if (!window.MutationObserver || !this.$refs['above-panel-content']?.$el)
        return;

      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(this.$refs['above-panel-content'].$el, {
        childList: true,
      });
    },
    handleMutations(mutations) {
      // Use requestAnimationFrame for better performance
      this.$nextTick(() => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            this.lastChild = mutation.target.lastChild;
            break; // only need last mutation's target
          }
        }
      });
    },
    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.lastChild = null;
    },
  },
};
</script>

<style lang="scss" scoped>
// Inspired by and currently equal to vuetify's $grid gutters.
$shift-length: $spacer * 6;
$shift-lengths: (
  'xs': $shift-length / 6,
  'sm': $shift-length / 3,
  'md': $shift-length / 2,
  'lg': $shift-length * 2/3,
  'xl': $shift-length,
);

@mixin shift-content-items($shift) {
  // REFACTOR: make generic instead of relying fully on above-panel / above-panel__item structure
  &.has-overhang + * {
  }

  .above-panel__item:not(.above-panel__item--full-width) {
    transform: translateY($shift);
  }
}

// REFACTOR: improve height animations https://carlanderson.xyz/how-to-animate-on-height-auto/ currently only works from/to 0
// REFACTOR: Use calculated height to set height of TheAppMenu prepend (operating company select container) for alignment

.above-panel {
  position: relative;
  margin-bottom: 0;
  width: 100%;
  z-index: 0; // create new stacking context for before element
  clip-path: polygon(
    0% 0%,
    100% 0%,
    100% 100vh,
    0% 100vh
  ); // prevent shadow above element
  max-height: 0; // make auto height animateable
  transition: all $primary-transition;

  &.is-compact::v-deep {
    @include shift-content-items(map-get($shift-lengths, 'xs'));
  }

  &.has-content {
    max-height: 400px; // MAGIC: most headers won't exceed this height, but it's close enough so animations are smoother
  }

  &.is-flat {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }

  &.has-scrolled-sibling {
    padding-right: var(--scrollbar-width);

    &::v-deep .above-panel__item--full-width {
      margin-right: calc(var(--scrollbar-width) * -1);
      width: calc(100% + var(--scrollbar-width));
      max-width: calc(100% + var(--scrollbar-width));
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--v-branding-base);
    transition: $primary-transition;
    transition-property: opacity, color;
    opacity: 1;
    z-index: -1;
  }
}

.above-panel::v-deep .above-panel__item {
  padding-top: 0;
  padding-bottom: 0;
  transform: translateY(0);
  transition: all $primary-transition;

  &.above-panel__item--full-width {
    padding: 0;
  }
}

@each $bp, $shift in $shift-lengths {
  @media #{map-get($display-breakpoints, #{$bp}-only)} {
    .above-panel::v-deep {
      @include shift-content-items($shift);
    }
  }
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  .above-panel {
    &::before {
      opacity: 0.3;
    }
  }
}
</style>
<style lang="css" scoped>
/deep/ .v-toolbar__content {
  display: none;
}
/* Chrome bug where there is a weird width animation in the header when jumping from
    a tab with scrollbar to one without (e.g. Project show Details -> Sub Projects) */
@media (min-width: 1184px) {
  .container {
    max-width: 1036px !important;
  }
}
@media (min-width: 1634px) {
  .container {
    max-width: 1184px !important;
  }
}
</style>
